// Star system registry. One source of truth for what systems exist,
// which one is currently active, and how to register new ones (the
// procedural generator will register at runtime once it lands).
//
// Sol stays as 'home' and the default fallback. The current system
// persists in localStorage so reloads land the player wherever they
// were, with a Sol fallback if the key is missing or unknown.

import { sol } from './catalog/sol.js';
import { alphaCentauri } from './catalog/alphaCentauri.js';
import { gargantua } from './catalog/gargantua.js';

const STORAGE_KEY = '3dsolarsystem:current-system';

const _registry = new Map();
let _currentId = 'sol';

/**
 * Register a system in the global registry. Called by the catalog
 * module at import time for hand-authored systems; called by the
 * procedural generator at runtime for seeded systems.
 *
 * @param {import('./schema.js').StarSystemData} data
 */
export function registerSystem(data) {
  if (!data || !data.id) {
    console.warn('[systems] registerSystem called without id', data);
    return;
  }
  _registry.set(data.id, data);
}

/**
 * @returns {import('./schema.js').StarSystemData[]} All registered systems.
 */
export function listSystems() {
  return Array.from(_registry.values());
}

export function getSystem(id) {
  return _registry.get(id) || null;
}

export function getCurrentSystem() {
  return _registry.get(_currentId) || _registry.get('sol') || null;
}

export function getCurrentSystemId() {
  return _currentId;
}

/**
 * Switch the active system. Persists the choice to localStorage so
 * reloads stay put. Returns false if the ID is unknown.
 *
 * Does not handle camera teleport or visual transition — the caller
 * (typically main.js) drives the warp animation around this call.
 *
 * @param {string} id
 * @returns {boolean}
 */
export function setCurrentSystem(id) {
  if (!_registry.has(id)) {
    console.warn(`[systems] Unknown system: ${id}`);
    return false;
  }
  _currentId = id;
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch (e) {
    // localStorage can throw in private-mode safari etc. Non-fatal.
  }
  return true;
}

/**
 * Read the persisted current system on startup. Falls back to 'sol'
 * if the key is missing or refers to an unknown system. Call once
 * during app init, after the catalog has registered.
 *
 * @returns {string} The resolved current system ID.
 */
export function restoreCurrentSystem() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && _registry.has(saved)) {
      _currentId = saved;
    } else {
      _currentId = 'sol';
    }
  } catch (e) {
    _currentId = 'sol';
  }
  return _currentId;
}

// Register the built-in catalog at module import time so the
// registry is populated by the time main.js asks for systems.
registerSystem(sol);
registerSystem(alphaCentauri);
registerSystem(gargantua);
